import { registerLocaleData } from "@angular/common";
import localeDa from "@angular/common/locales/da";
import { Injector } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";

import { mdiClose } from "@mdi/js";
import "hammerjs"; // Kræves for at kunne dragge MatSlideToggle

import { forkJoin, of } from "rxjs";
import { catchError, filter, first } from "rxjs/operators";

import { erstatEmailValidatorMedDffEdbUdgave } from "@dffedb/forms";
import { SvgIconRegistry } from "@dffedb/util";
import { EBoksPopupService } from "@e-forsyning/common/e-boks";
import { EforsyningMenuService } from "@e-forsyning/common/eforsyning-menu";

import { KontaktoplysningerPopupService } from "@e-forsyning/common/kontaktoplysninger-popup";
import { initUmbracoIndstillinger } from "@e-forsyning/common/umbraco-indstillinger";
import { UmbracoIndstillinger } from "@e-forsyning/common/umbraco-indstillinger/model/umbraco-indstillinger.model";
import { UmbracoIndstillingerObserver } from "@e-forsyning/common/umbraco-indstillinger";
import { UmbracoIndstillingerRepository } from "@e-forsyning/common/umbraco-indstillinger/umbraco-indstillinger.repository";
import { UmbracoMenuObserver } from "@e-forsyning/common/eforsyning-menu/observers/umbraco-menu.observer";
import { AppserverInstallationCredentialsRepository } from "@e-forsyning/common/credentials";
import { RolleBodyClassService, RouteBodyClassService } from "@e-forsyning/common/util";
import { AktuelBrugersInstallationerService } from "@e-forsyning/common/installation/aktuel-brugers-installationer.service";
import { UmbracoHttpClient } from "@e-forsyning/common/http";
import { MenuItem } from "@dffedb/ui";

registerLocaleData(localeDa);
erstatEmailValidatorMedDffEdbUdgave();

export class AppModule {
    constructor(svgIconRegistry: SvgIconRegistry) {
        svgIconRegistry.registerIcon(mdiClose, "app-info-close", "dummy-to-trigger-build-v1");
    }
}

export function appInit(injector: Injector): () => void {
    return () => {
        initUmbracoIndstillinger(injector, (window as any).initUmbracoIndstillinger);
        ensureUmbracoIndstillingerFromCredentials(injector);
        initBodyClassServices(injector);
        initPopups(injector);
        injector.get(EforsyningMenuService).init();
    };
}

function initBodyClassServices(injector: Injector): void {
    injector.get(RouteBodyClassService).setup();
    injector.get(RolleBodyClassService).setup();
}

function initPopups(injector: Injector): void {
    const router = injector.get(Router);
    const eboksPopupService = injector.get(EBoksPopupService);
    const kontaktoplysningerPopupService = injector.get(KontaktoplysningerPopupService);
    const aktuelBrugersInstallationerService = injector.get(AktuelBrugersInstallationerService);

    router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(async () => {
        const installationer = await aktuelBrugersInstallationerService.select().pipe(first()).toPromise();
        if (installationer.length === 1) {
            eboksPopupService.aabnHvisIkkeAlleredeTilmeldt().then(() => {
                kontaktoplysningerPopupService.aabnHvisIkkeAlleredeTilmeldt();
            });
        }
    });
}

function ensureUmbracoIndstillingerFromCredentials(injector: Injector): void {
    const credentials = injector.get(AppserverInstallationCredentialsRepository).hent();
    if (!credentials?.forsyningNodeId) {
        return;
    }

    const umbracoIndstillingerObserver = injector.get(UmbracoIndstillingerObserver);
    umbracoIndstillingerObserver.valueChanges.pipe(first()).subscribe((current) => {
        if (current?.aktuelEforsyningNodeId) {
            return;
        }

        const umbracoIndstillingerRepository = injector.get(UmbracoIndstillingerRepository);
        const umbracoMenuObserver = injector.get(UmbracoMenuObserver);
        const umbracoHttpClient = injector.get(UmbracoHttpClient);

        const indstillinger$ = umbracoIndstillingerRepository.hent(credentials.forsyningNodeId);
        const eforsyningMenu$ = umbracoHttpClient.get<MenuItem>(`HentEforsyningMenu?id=${credentials.forsyningNodeId}`).pipe(catchError(() => of(null)));
        const forsyningWebMenu$ = umbracoHttpClient.get<MenuItem[]>(`HentForsyningWebMenu?id=${credentials.forsyningNodeId}`).pipe(catchError(() => of(null)));

        forkJoin({ indstillinger: indstillinger$, eforsyningMenu: eforsyningMenu$, forsyningWebMenu: forsyningWebMenu$ })
            .pipe(first())
            .subscribe({
                next: ({ indstillinger, eforsyningMenu, forsyningWebMenu }) => {
                    const model = Object.assign(new UmbracoIndstillinger(), indstillinger);
                    if (model.aktuelEforsyningNodeId == null) {
                        model.aktuelEforsyningNodeId = credentials.forsyningNodeId;
                    }
                    if (!model.aktuelEforsyning && model.eforsyninger && model.eforsyninger.length) {
                        const valgtEforsyning = model.eforsyninger.find((e) => e.nodeId === credentials.forsyningNodeId) || model.eforsyninger[0];
                        if (valgtEforsyning) {
                            model.aktuelEforsyningNodeId = valgtEforsyning.nodeId;
                        }
                    }
                    if (model.aktuelEforsyning && !model.aktuelEforsyning.forsyningId && credentials.forsyningId) {
                        model.aktuelEforsyning.forsyningId = credentials.forsyningId;
                    }
                    umbracoIndstillingerObserver.next(model);
                    umbracoMenuObserver.next({ eforsyninger: eforsyningMenu, forsyningWeb: forsyningWebMenu });
                },
                error: (error) => {
                    console.error("[login-debug] Failed to hydrate UmbracoIndstillinger from credentials", error);
                }
            });
    });
}
